import React, { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';

// WalletTopUp — the salon's money surface (Khari, 2026-08-03: "the customer
// has not entered payment info at this point").
//
// The gate is deliberate: the treatment costs credits at the exact moment the
// player is invested. What was missing is the way to PAY standing in the
// chair. This panel is that way, and it follows design law 1 — money is
// conventional UI. It never touches card details: paid plans carry
// monthly_credits, and the HOSTED checkout (the same one the website uses)
// takes the card in its own tab. This panel just opens the door and watches
// the wallet.
//
// Flow: scene reports wallet.short → panel lists the paid plans → pick one →
// checkout opens in a new tab → the panel polls the balance → when the
// credits land it tells the scene (a world note), Sassy takes the yes she
// already has, and the panel gets out of the way.

export default function WalletTopUp({ wallet, onCleared, onDismiss }) {
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(null);       // priceId being checked out
  const [watching, setWatching] = useState(false);
  const [note, setNote] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    let dead = false;
    api.plans()
      .then((d) => {
        if (dead) return;
        // Paid plans only, cheapest first — the free tier can't fund a chair.
        const paid = (d?.plans || []).filter((p) => (p.price_cents || 0) > 0 && p.priceId);
        setPlans(paid);
      })
      .catch(() => { if (!dead) setPlans([]); });
    return () => { dead = true; };
  }, []);

  // Watch the wallet after checkout opens: webhook lands → balance covers the
  // treatment → hand back to the scene. Bounded to ~5 minutes of watching.
  useEffect(() => () => clearInterval(pollRef.current), []);
  function watchBalance() {
    setWatching(true);
    let ticks = 0;
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 75) { clearInterval(pollRef.current); setWatching(false); setNote('Still not seeing it — finished checkout? Give it a few seconds and tap a plan again.'); return; }
      try {
        const d = await api.creditBalance();
        const total = d?.balance?.total ?? 0;
        if (total >= (wallet?.required || 0)) {
          clearInterval(pollRef.current);
          onCleared?.(total);
        }
      } catch { /* next tick */ }
    }, 4000);
  }

  async function buy(plan) {
    if (busy) return;
    setBusy(plan.priceId); setNote('');
    try {
      const { res, data } = await api.planCheckout({
        priceId: plan.priceId, planId: plan.plan_tier, billingCycle: 'monthly', source: 'world',
      });
      if (!res.ok || !data?.url) throw new Error(data?.error || 'Checkout failed to open');
      window.open(data.url, '_blank', 'noopener');
      setNote('Checkout opened in a new tab — I’ll watch for your credits here.');
      watchBalance();
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy(null);
    }
  }

  if (!wallet?.short) return null;

  return (
    <div style={wrap}>
      {/* Never a trap (design law 2): the wall can always be waved off — the
          conversation stays, the chair keeps, they come back when they're right. */}
      {onDismiss && (
        <button onClick={onDismiss} aria-label="Not now" style={{
          position: 'absolute', top: 6, right: 8, background: 'none', border: 'none',
          color: '#ffe9c4', opacity: 0.55, cursor: 'pointer', fontSize: 13, padding: 4,
        }}>✕</button>
      )}
      <div style={{ font: '700 12.5px system-ui', color: '#ffd27d' }}>
        The treatment is {wallet.required} credits — you're at {wallet.balance}.
      </div>
      <div style={{ font: '600 11px system-ui', opacity: 0.6, margin: '3px 0 9px' }}>
        Plans come with monthly credits. Card goes in on the checkout page, never here.
      </div>

      {plans === null && <div style={{ font: '600 11.5px system-ui', opacity: 0.6 }}>Getting the plans…</div>}
      {plans !== null && plans.length === 0 && (
        <div style={{ font: '600 11.5px system-ui', opacity: 0.6 }}>
          No plans available right now — top up from the website and come back.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(plans || []).map((p) => (
          <button key={p.priceId} style={{ ...planBtn, opacity: busy && busy !== p.priceId ? 0.5 : 1 }} disabled={!!busy || watching} onClick={() => buy(p)}>
            <span style={{ font: '800 12.5px system-ui' }}>{p.display_name || p.plan_name}</span>
            <span style={{ font: '600 11px system-ui', opacity: 0.75 }}>
              {p.monthly_credits ? `${p.monthly_credits} credits/mo · ` : ''}${(p.price_cents / 100).toFixed(2)}/mo
            </span>
          </button>
        ))}
      </div>

      {watching && (
        <div style={{ font: '600 11.5px system-ui', color: '#7dff9d', marginTop: 8 }}>
          Watching for your credits…
        </div>
      )}
      {note && <div style={{ font: '600 11px system-ui', color: '#ffd27d', marginTop: 6, lineHeight: 1.4 }}>{note}</div>}
    </div>
  );
}

const wrap = {
  position: 'relative',
  margin: '0 12px 8px', padding: '10px 12px', borderRadius: 12,
  border: '1px solid rgba(255,210,125,0.45)', background: 'rgba(40,26,10,0.6)',
};
const planBtn = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
  padding: '9px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
  border: '1px solid rgba(255,178,94,0.4)', background: 'rgba(255,178,94,0.1)',
  color: '#ffe9c4',
};
